/**
 * Mobile-specific MUI component overrides.
 *
 * These overrides improve touch targets, spacing, and overall
 * usability on mobile devices. They're applied when the app
 * detects it's running on a small screen.
 */

const mobileOverrides = {
  MuiButton: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          minHeight: 44, // iOS touch target guideline
          borderRadius: 10,
        },
      },
      sizeSmall: {
        '@media (max-width: 767px)': {
          minHeight: 36,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          padding: 10, // Larger touch target
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          '& .MuiInputBase-root': {
            minHeight: 48,
            fontSize: '1rem', // Prevent iOS zoom on focus
          },
          '& .MuiInputBase-input': {
            fontSize: '1rem', // Must be 16px+ to prevent iOS zoom
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        '@media (max-width: 767px)': {
          fontSize: '1rem',
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          minHeight: 48, // Touch target
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          minHeight: 48,
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          minHeight: 44,
          minWidth: 'auto',
          padding: '8px 12px',
          fontSize: '0.8125rem',
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          minHeight: 44,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          borderRadius: 12,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        '@media (max-width: 767px)': {
          margin: 16,
          width: 'calc(100% - 32px)',
          maxWidth: 'none',
          borderRadius: 16,
          maxHeight: 'calc(100vh - 32px)',
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          padding: '16px',
          fontSize: '1.1rem',
        },
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          padding: '8px 16px',
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          padding: '12px 16px',
          flexWrap: 'wrap',
          gap: 8,
          '& > :not(:first-of-type)': {
            marginLeft: 0,
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          height: 30,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          padding: '8px 10px',
          fontSize: '0.8125rem',
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          fontSize: '0.8125rem',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.7rem',
          },
          '& .MuiDataGrid-cell': {
            padding: '4px 8px',
          },
          '& .MuiDataGrid-footerContainer': {
            minHeight: 44,
          },
        },
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      container: {
        '@media (max-width: 767px)': {
          // Tighten grid spacing on mobile
          '&.MuiGrid-spacing-xs-3': {
            margin: -8,
            '& > .MuiGrid-item': {
              padding: 8,
            },
          },
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          // Ensure Paper components don't overflow
          maxWidth: '100%',
          overflow: 'hidden',
        },
      },
    },
  },
  MuiStack: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          maxWidth: '100%',
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '@media (max-width: 767px)': {
          '& .MuiInputBase-root': {
            fontSize: '1rem',
          },
        },
      },
      listbox: {
        '@media (max-width: 767px)': {
          maxHeight: '40vh',
          '& .MuiAutocomplete-option': {
            minHeight: 44,
          },
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        '@media (max-width: 767px)': {
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '50vh',
        },
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        '@media (max-width: 767px)': {
          maxWidth: 'calc(100vw - 32px)',
        },
      },
    },
  },
};

export default mobileOverrides;
